"use client";

import { useCustomer } from "@/contexts/customer-context";
import { Button } from "@/modules/common/components/button";
import { FavoriteButton } from "@/modules/favorites/components/favorite-button";
import { useCart } from "@/src/contexts/cart-context";
import { addToCart } from "@/modules/cart/actions/cart";
import {
  useCurrencyStore,
  useLocationStore,
  useProductModalStore,
  useToastStore,
  useRegionStore,
} from "@/store/index";
import { ShoppingCart } from "lucide-react";
import Image from "next/image";
import { useParams, useRouter, useSearchParams } from "next/navigation";
import { useState } from "react";
import ProductRating from "./product-rating";
import { StoreProduct } from "@medusajs/types";
import { useReviews } from "../hooks/use-reviews";
import { Skeleton } from "@/modules/common/components/skeleton";
import { canAddToCart } from "@/lib/util/stock";

function HighlightMatch({ text, query }: { text: string; query: string }) {
  if (!query) return <>{text}</>;
  const parts = text.split(new RegExp(`(${query})`, "gi"));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <span key={i} className="bg-yellow-200 font-bold">
            {part}
          </span>
        ) : (
          part
        )
      )}
    </>
  );
}

interface ProductCardProps {
  product: StoreProduct;
  price: string;
  originalPrice?: string;
}

export function ProductCard({
  product,
  price,
  originalPrice,
}: ProductCardProps) {
  const { openModal } = useProductModalStore();
  const { refreshCart, cart } = useCart();
  const { customer } = useCustomer();
  const params = useParams();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isAdding, setIsAdding] = useState(false);
  const { currencyCode } = useCurrencyStore();
  const { regionId } = useRegionStore();
  const { province, municipality } = useLocationStore();
  const { showToast } = useToastStore();
  const { data, isLoading } = useReviews({ productId: product.id });

  const { title, thumbnail } = product;
  const query = searchParams.get("q") || "";
  const variantId = product.variants?.[0]?.id;
  const stock = product.variants?.[0]?.inventory_quantity || 0;
  const addDisabled = !variantId || !canAddToCart(cart, variantId, stock, 1);

  const handleCardClick = () => {
    openModal({
      ...product,
      price,
      originalPrice,
    });
  };

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!customer) {
      showToast("Inicia sesión para añadir productos al carrito", "err");
      router.push(`/${(params.countryCode as string) || "cu"}/login`);
      return;
    }

    if (isAdding) return;

    setIsAdding(true);
    try {
      if (!variantId) {
        console.error("No variant ID found for product");
        return;
      }

      await addToCart({
        variantId,
        quantity: 1,
        countryCode: (params.countryCode as string) || "cu",
        regionId: regionId || undefined,
        currency_code: currencyCode.toLowerCase(),
        province,
        municipality,
      });

      await refreshCart();
      // Optional: Show success toast
    } catch (error: unknown) {
      const err = error as Error;
      console.error("Error adding to cart:", err.message);

      if (err.message) {
        showToast(err.message, "err");
      }
      // Optional: Show error toast
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <div
      onClick={handleCardClick}
      className="group border-green-main relative flex cursor-pointer flex-col overflow-hidden rounded-lg border bg-white p-4 transition-all hover:shadow-lg"
    >
      {customer && (
        <div className="absolute top-4 right-4 z-10">
          <FavoriteButton productId={product.id} />
        </div>
      )}

      <div className="relative aspect-square w-full overflow-hidden rounded-md bg-gray-100">
        {thumbnail ? (
          <Image
            src={thumbnail}
            alt={title}
            fill
            className="object-contain object-center transition-transform group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-gray-400">
            No Image
          </div>
        )}
      </div>

      <div className="mt-4 flex flex-1 flex-col justify-end">
        <div className="flex justify-between">
          <div>
            <h3 className="group-hover:text-green-main text-sm font-medium text-gray-900">
              <HighlightMatch text={title} query={query} />
            </h3>
            {product.subtitle && (
              <p className="mt-1 text-xs text-gray-500">
                <HighlightMatch text={product.subtitle} query={query} />
              </p>
            )}
            {stock > 0 && (
              <p className="mt-1 text-xs text-gray-500">
                Stock: {stock} artículo{stock !== 1 ? "s" : ""} disponible
                {stock !== 1 ? "s" : ""}
              </p>
            )}
          </div>
          {isLoading ? (
            <Skeleton className="h-4 w-20" />
          ) : (
            <ProductRating
              product_id={product.id}
              rating={data?.average_rating ?? 0}
            />
          )}
        </div>

        <div className="mt-2 flex items-baseline gap-2">
          <span className="text-orange-main text-lg font-bold">{price}</span>
          {originalPrice && (
            <span className="text-sm text-gray-400 line-through">
              {originalPrice}
            </span>
          )}
        </div>

        <Button
          onClick={handleAddToCart}
          disabled={isAdding || addDisabled}
          className="bg-green-main mt-3 w-full gap-2 hover:bg-[#047a2e] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ShoppingCart className="h-4 w-4" />
          {isAdding ? "Añadiendo..." : stock <= 0 ? "Sin stock" : "Añadir"}
        </Button>
      </div>
    </div>
  );
}
